import React, { useEffect, useMemo, useState } from "react";
import { Badge, Button, Col, Form, Row } from "react-bootstrap";
import { FiArrowDown, FiArrowUp, FiPlus, FiX } from "react-icons/fi";
import type { Kpi, KpiDependent } from "../types/kpi";
import type {
  KpiDependsOn,
  KpiOperand,
  KpiOperation,
} from "../types/kpiEntryMode";
import { ENTRY_MODE_CALCULATED, KPI_OPERATIONS, buildFormulaPreview } from "../types/kpiEntryMode";
import { subtleBadgeProps } from "@core/utils/subtleBadge";
import {
  formatKpiDependentContext,
  kpiDependentRowKey,
} from "../utils/kpiDependentDisplay";
import { fetchKpiActivitiesAsync } from "../services/kpiSvc";
import { useScopeId } from "@core/hooks/useRouteParamId";

type Props = {
  operation: KpiOperation | string;
  operands: KpiOperand[];
  availableKpis: Kpi[];
  /** Exclude the KPI being edited from operand choices. */
  excludeKpiId?: number;
  /** Calculated KPIs that use this KPI as an operand (from used_by). */
  usedBy?: KpiDependent[];
  /** Source activities + templates (from depends_on). */
  dependsOn?: KpiDependsOn[];
  onChangeOperation: (value: KpiOperation | string) => void;
  onChangeOperands: (next: KpiOperand[]) => void;
};

function renumber(operands: KpiOperand[]): KpiOperand[] {
  return operands.map((op, idx) => ({ ...op, list_order: idx }));
}

const KpiFormulaEditor: React.FC<Props> = ({
  operation,
  operands,
  availableKpis,
  excludeKpiId,
  usedBy,
  dependsOn,
  onChangeOperation,
  onChangeOperands,
}) => {
  const nationId = useScopeId() ?? 0;
  const kpiNameById = useMemo(() => {
    const map = new Map<number, string>();
    for (const k of availableKpis) map.set(k.kpi_id, k.kpi_name);
    return map;
  }, [availableKpis]);

  const attachedPairs = useMemo(
    () =>
      new Set(
        operands.map((o) => `${o.source_kpi_id}:${o.source_activity_id}`)
      ),
    [operands]
  );

  const candidates = useMemo(
    () =>
      availableKpis.filter(
        (k) => k.kpi_id !== excludeKpiId && isNumericCompatibleCandidate(k)
      ),
    [availableKpis, excludeKpiId]
  );

  const [pickKpiId, setPickKpiId] = useState<number | "">("");
  const [pickActivityId, setPickActivityId] = useState<number | "">("");
  const [activityOptions, setActivityOptions] = useState<
    Array<{ activity_id: number; activity_name: string }>
  >([]);
  const [activitiesLoading, setActivitiesLoading] = useState(false);

  useEffect(() => {
    setPickActivityId("");
    setActivityOptions([]);
    if (pickKpiId === "" || !nationId) return;
    let cancelled = false;
    setActivitiesLoading(true);
    (async () => {
      try {
        const rows = await fetchKpiActivitiesAsync(nationId, Number(pickKpiId));
        if (!cancelled) {
          setActivityOptions(
            rows.filter(
              (a) => !attachedPairs.has(`${pickKpiId}:${a.activity_id}`)
            )
          );
        }
      } catch {
        if (!cancelled) setActivityOptions([]);
      } finally {
        if (!cancelled) setActivitiesLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [pickKpiId, nationId, attachedPairs]);

  const addOperand = () => {
    if (pickKpiId === "" || pickActivityId === "") return;
    const kpi = availableKpis.find((k) => k.kpi_id === Number(pickKpiId));
    const activity = activityOptions.find(
      (a) => a.activity_id === Number(pickActivityId)
    );
    if (!kpi || !activity) return;
    onChangeOperands(
      renumber([
        ...operands,
        {
          source_kpi_id: kpi.kpi_id,
          source_kpi_name: kpi.kpi_name,
          source_activity_id: activity.activity_id,
          source_activity_name: activity.activity_name,
          list_order: operands.length,
        },
      ])
    );
    setPickKpiId("");
    setPickActivityId("");
  };

  const move = (index: number, dir: -1 | 1) => {
    const target = index + dir;
    if (target < 0 || target >= operands.length) return;
    const next = [...operands];
    const [item] = next.splice(index, 1);
    next.splice(target, 0, item);
    onChangeOperands(renumber(next));
  };

  const remove = (index: number) => {
    onChangeOperands(renumber(operands.filter((_, i) => i !== index)));
  };

  const preview = buildFormulaPreview(operation, operands, kpiNameById);

  return (
    <div data-testid="kpi-formula-editor">
      <Form.Group className="mb-3" controlId="kpiOperation">
        <Form.Label>Operation</Form.Label>
        <Form.Select
          value={operation || ""}
          onChange={(e) => onChangeOperation(e.target.value)}
        >
          <option value="">Select operation</option>
          {KPI_OPERATIONS.map((op) => (
            <option key={op} value={op}>
              {op}
            </option>
          ))}
        </Form.Select>
      </Form.Group>

      <Form.Group className="mb-3">
        <Form.Label>Operands (ordered)</Form.Label>
        <Row className="g-2 align-items-end">
          <Col xs={12} lg>
            <Form.Label className="small text-muted mb-1">KPI</Form.Label>
            <Form.Select
              value={pickKpiId === "" ? "" : String(pickKpiId)}
              onChange={(e) =>
                setPickKpiId(e.target.value === "" ? "" : Number(e.target.value))
              }
              data-testid="kpi-operand-picker"
            >
              <option value="">Select a KPI…</option>
              {candidates.map((k) => {
                const mode = (k.entry_mode ?? "MANUAL").toUpperCase();
                const label =
                  mode === ENTRY_MODE_CALCULATED
                    ? `${k.kpi_name} (Calculated)`
                    : k.kpi_name;
                return (
                  <option key={k.kpi_id} value={k.kpi_id}>
                    {label}
                  </option>
                );
              })}
            </Form.Select>
          </Col>
          <Col xs={12} lg>
            <Form.Label className="small text-muted mb-1">Activity</Form.Label>
            <div className="d-flex gap-2">
              <Form.Select
                className="flex-grow-1"
                value={pickActivityId === "" ? "" : String(pickActivityId)}
                onChange={(e) =>
                  setPickActivityId(
                    e.target.value === "" ? "" : Number(e.target.value)
                  )
                }
                disabled={pickKpiId === "" || activitiesLoading}
                data-testid="kpi-operand-activity-picker"
              >
                <option value="">
                  {pickKpiId === ""
                    ? "Select a KPI first…"
                    : activitiesLoading
                      ? "Loading activities…"
                      : activityOptions.length === 0
                        ? "Assign this KPI to an activity first"
                        : "Select an activity…"}
                </option>
                {activityOptions.map((a) => (
                  <option key={a.activity_id} value={a.activity_id}>
                    {a.activity_name}
                  </option>
                ))}
              </Form.Select>
              <Button
                variant="outline-primary"
                className="btn-action flex-shrink-0"
                onClick={addOperand}
                disabled={pickKpiId === "" || pickActivityId === ""}
                data-testid="kpi-operand-add"
              >
                <FiPlus className="me-1" />
                Add
              </Button>
            </div>
          </Col>
        </Row>
      </Form.Group>

      {operands.length === 0 ? (
        <div className="text-muted small mb-3">
          No operands selected. Each operand needs a KPI and the activity it
          comes from.
        </div>
      ) : (
        <div className="d-flex flex-column gap-2 mb-3">
          {operands.map((op, index) => {
            const name =
              op.source_kpi_name ||
              kpiNameById.get(op.source_kpi_id) ||
              `KPI #${op.source_kpi_id}`;
            const activity = op.source_activity_name || `Activity #${op.source_activity_id}`;
            return (
              <div
                key={`${op.source_kpi_id}-${op.source_activity_id}-${index}`}
                className="d-flex align-items-center gap-2 border rounded px-2 py-1"
                data-testid={`kpi-operand-row-${index}`}
              >
                <Badge {...subtleBadgeProps("secondary")} pill>
                  {index + 1}
                </Badge>
                <span className="flex-grow-1 text-break">
                  {name} · {activity}
                </span>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  disabled={index === 0}
                  onClick={() => move(index, -1)}
                  aria-label="Move up"
                >
                  <FiArrowUp />
                </Button>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  disabled={index === operands.length - 1}
                  onClick={() => move(index, 1)}
                  aria-label="Move down"
                >
                  <FiArrowDown />
                </Button>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  className="ntcog-table-action-btn--danger"
                  onClick={() => remove(index)}
                  aria-label="Remove operand"
                >
                  <FiX />
                </Button>
              </div>
            );
          })}
        </div>
      )}

      {preview ? (
        <div
          className="alert alert-light border small mb-0"
          data-testid="kpi-formula-preview"
        >
          <span className="text-muted">This calculates from: </span>
          <strong>{preview}</strong>
        </div>
      ) : null}

      {dependsOn !== undefined ? (
        <div className={preview ? "mt-3" : "mt-0"} data-testid="kpi-depends-on">
          <Form.Label className="mb-2">Depends on</Form.Label>
          {dependsOn.length > 0 ? (
            <ul className="mb-0 small">
              {dependsOn.map((dep) => (
                <li key={dep.activity_id}>
                  <strong>{dep.activity_name}</strong>
                  {dep.templates.length > 0 ? (
                    <span className="text-muted">
                      {" "}
                      — used on {dep.templates.length} template
                      {dep.templates.length === 1 ? "" : "s"}
                      {": "}
                      {dep.templates
                        .map((t) => t.report_template_name)
                        .join(", ")}
                    </span>
                  ) : (
                    <span className="text-muted"> — not on any template yet</span>
                  )}
                </li>
              ))}
            </ul>
          ) : (
            <div className="text-muted small mb-0">
              No source activities yet. Add operands above.
            </div>
          )}
        </div>
      ) : null}

      {usedBy !== undefined ? (
        <div className="mt-3" data-testid="kpi-used-by">
          <Form.Label className="mb-2">Used by</Form.Label>
          {usedBy.length > 0 ? (
            <ul className="mb-0 small">
              {usedBy.map((dep, index) => {
                const context = formatKpiDependentContext(dep);
                return (
                  <li key={kpiDependentRowKey(dep, index)}>
                    <strong>{dep.kpi_name}</strong>
                    {context ? (
                      <span className="text-muted"> ({context})</span>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          ) : (
            <div className="text-muted small mb-0">
              Not used by other calculated KPIs.
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
};

function isNumericCompatibleCandidate(k: Kpi): boolean {
  const n = (k.data_type_name || "").toUpperCase();
  if (!n) return true;
  return [
    "INTEGER",
    "INTEGER_SIGNED",
    "INTEGER_UNSIGNED",
    "DECIMAL",
    "DECIMAL_SIGNED",
    "DECIMAL_UNSIGNED",
    "FLOAT",
    "PERCENTAGE",
    "CURRENCY",
    "BOOLEAN",
  ].some((token) => n === token || n.includes(token));
}

export default KpiFormulaEditor;
